import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { CalendarView } from "angular-calendar";
import { Observable, Subject, zip } from "rxjs";
import { Appointment } from "src/app/core/model/v2/appointment/appointment";
import { PractitionerDayOff } from "src/app/core/model/v2/practitioner/practitioner-day-off";
import { PracticeService } from "src/app/core/services/v2/practice.service";
import { AppointmentsService as ApptService } from "src/app/core/services/v2/appointments.service";
import { PhysicianOptionService } from "../shared/physician-option/physician-option.service";
import { AppointmentStatus } from "src/app/core/model/v2/appointment/appointment-status";
import { AppointmentSlots } from "src/app/core/model/v2/appointment/appointmentSlots";
import { CustomCalendarEvent } from "./custom-models/custom-calendar-event";
import moment from "moment";
import { AppointmentTime } from "src/app/core/model/v2/appointment/appointment-time";
import { Patient } from "src/app/core/models/patient/patient";
import { CreateAppointment } from "src/app/core/model/v2/appointment/create-appointment";
import { ReferralsService } from "src/app/referrals/referrals.service";
import { Referral } from "src/app/core/model/referral";
import {
  differenceInCalendarDays,
  differenceInDays,
  isThisSecond,
  startOfDay,
} from "date-fns";
import { NotificationService } from "src/app/shared/notification-center/notification.service";
import { MatSnackBar } from "@angular/material";

@Injectable({
  providedIn: "root",
})
export class AppointmentsService {
  appointmentId: number;
  toggleSidebar: boolean = false;
  selectedFormList: any = [];
  selectedDate: Date = new Date();
  startHour: number = 8;
  endHour: number = 17;
  selectedCalendarView: CalendarView = CalendarView.Day;
  refreshCalendar: Subject<any> = new Subject();
  selectedEventsTitle: string = "";
  isRescheduling: boolean = false;
  isNewAppointment: boolean = false;
  totalAppointments: number = 0;
  totalNewAppointments: number = 0;
  totalReferralAppointments: number = 0;
  totalEstablishedAppointments: number = 0;
  noAppointmentsText: string = "No appointments confirmed for this day";

  private appointments: Appointment[] = [];
  practitionerDayOff: PractitionerDayOff[] = [];
  events: CustomCalendarEvent[] = [];
  selectedEvents: CustomCalendarEvent[] = [];
  onTimeEvents: CustomCalendarEvent[] = [];
  declinedEvents: CustomCalendarEvent[] = [];
  hasDeclinedEvents: boolean = false;
  currentEvent: CustomCalendarEvent;
  currentFilter: string = "";
  referrals: Referral[] = [];

  selectEvent$: Subject<Date> = new Subject<Date>();
  restartTimeout$: Subject<void> = new Subject<void>();

  constructor(
    private appointmentsService: ApptService,
    private referralService: ReferralsService,
    private practiceService: PracticeService,
    private physicianOptionService: PhysicianOptionService,
    private snackBar: MatSnackBar
  ) {}

  setAppointment(obj, details: Object) {
    this[obj] = details;
  }

  getAppointment(obj) {
    return this[obj];
  }

  getCalendarAppointments(): Observable<void> {
    const observable = new Observable<void>((observer) => {
      const context = this;

      this.restartTimeout$.next();
      this.getReferrals();

      context.events = [];
      this.getDayOff().subscribe(
        (dayOff) => {
          context.practitionerDayOff = dayOff;
          dayOff.forEach((d) => {
            this.createDayOffEvent(d);
          });
          context.getAppointments().subscribe(
            (appointments) => {
              this.appointments = [];
              this.appointments = appointments;

              this.getAppointmentsNumber(appointments);

              context.CreateEventList(appointments);

              const hasFilterSelected: string = localStorage.getItem(
                "appointmentFilterSelected"
              );
              if (hasFilterSelected && hasFilterSelected !== "") {
                const filterClone = JSON.parse(
                  JSON.stringify(hasFilterSelected)
                );
                this.filterAppointments(filterClone);
              }

              const hasPatientSelected: Patient = JSON.parse(
                localStorage.getItem("patientSelected")
              );
              if (hasPatientSelected) {
                this.selectPatient(hasPatientSelected);
              }

              observer.next();
              observer.complete();
            },
            (error) => {
              console.error(error);
              observer.error(error);
            }
          );
        },
        (error) => {
          console.error(error);
          observer.error(error);
        }
      );
    });

    return observable;
  }

  getSummaryAppointments(): void {
    this.getReferrals();
    this.getAppointments().subscribe((appointments) => {
      this.getAppointmentsNumber(appointments);
      if (
        this.appointments.length > 0 &&
        this.appointments.length < appointments.length
      ) {
        const newAppointments: Appointment[] = [];
        appointments.forEach((appt) => {
          const exists = this.appointments.find((x) => x.id === appt.id);
          if (!exists) {
            newAppointments.push(appt);
          }
        });

        if (newAppointments.length > 0) {
          this.CreateEventList(newAppointments);
        }
      }
      this.appointments = appointments;
    });
  }

  getAppointments(): Observable<Appointment[]> {
    const observable = new Observable<Appointment[]>((observer) => {
      const practitionerId = this.physicianOptionService.getPhysician().id;
      const healthSystemId = this.physicianOptionService.getHealthSystem().id;

      if (practitionerId === undefined && healthSystemId === undefined) {
        return;
      }

      this.appointmentsService
        .getAppointments(practitionerId, healthSystemId)
        .subscribe(
          (res) => {
            if (res) {
              const appointments = res["appointmentDetails"];
              observer.next(appointments);
              observer.complete();
            } else {
              this.snackBar.open(
                "An error occur to load appointment data. Try again later",
                "Ok"
              );
            }

            observer.error();
          },
          (error) => {
            console.error(error);
            observer.error(error);
          }
        );
    });

    return observable;
  }

  getReferrals(): void {
    if (!this.physicianOptionService.hasValues()) {
      return;
    }

    var requestFilter = {
      PractitionerId: this.physicianOptionService.getPhysician().id,
      HealthSystemId: this.physicianOptionService.getHealthSystem().id,
      Criteria: "",
      pageIndex: 1,
      itemsCount: 999,
    };

    this.referralService
      .getPractitionerToReferralListNoAppointments(requestFilter)
      .subscribe(
        (response) => {
          if (response["response"]["isSuccess"]) {
            this.totalReferralAppointments = response["totalRecords"];
            this.referrals = response["practitionerReferralDetail"];
            this.getTotalAppointmentsNumber();
          } else {
            this.snackBar.open(
              "An error occur to load referral data. Try again later",
              "Ok"
            );
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  getDayOff(): Observable<PractitionerDayOff[]> {
    const observable = new Observable<PractitionerDayOff[]>((observer) => {
      const practitionerId = this.physicianOptionService.getPhysician().id;
      const healthSystemId = this.physicianOptionService.getHealthSystem().id;

      if (practitionerId === undefined && healthSystemId === undefined) {
        return;
      }

      this.practiceService
        .getPractitionerDayOff(practitionerId, healthSystemId)
        .subscribe(
          (dayOff) => {
            if (dayOff.response.isSuccess) {
              observer.next(dayOff.practitionerDayOffDetail);
              observer.complete();
            } else {
              this.snackBar.open(
                "An error occur to load practitioner day off data. Try again later",
                "Ok"
              );
            }
            observer.error();
          },
          (error) => {
            console.error(error);
            observer.error(error);
          }
        );
    });

    return observable;
  }

  getTodaysNewAppointments(): CustomCalendarEvent[] {
    const todaysNewEvents = [];
    const filteredAppointments = this.appointments.filter(
      (appt) =>
        appt.type === "NEW" &&
        moment(appt.date).isSame(moment(), "days") &&
        appt.status === AppointmentStatus.APPOINTMENT_SCHEDULED
    );
    filteredAppointments.forEach((appt) => {
      const event = this.createEvent(appt);
      todaysNewEvents.push(event);
    });
    return todaysNewEvents;
  }

  filterAppointments(type: string) {
    this.clearSelectedEvents();
    this.clearEvents();
    this.selectedEventsTitle = type;
    this.currentFilter = type;

    if (type.toUpperCase() == "REFERRAL") {
      this.noAppointmentsText = "No Referral Requests";
      this.selectReferral();
      return;
    } else if (type.toUpperCase() == "NEW") {
      this.noAppointmentsText = "No Appointment Request for New Patients";
    } else if (type.toUpperCase() == "ESTABLISHED") {
      this.noAppointmentsText =
        "No Appointment Request for Establisthed Patients";
    } else {
      this.noAppointmentsText = "No appointments confirmed for this day";
    }

    let filteredAppointments = this.appointments.filter(
      (appt) =>
        appt.type === type &&
        moment(appt.date).isSameOrAfter(
          moment(new Date()).format("MM/DD/YYYY")
        ) &&
        appt.status !== AppointmentStatus.APPOINTMENT_CANCELLED &&
        appt.status !== AppointmentStatus.APPOINTMENT_SCHEDULED
    );
    filteredAppointments.forEach((appt) => {
      const event = this.createEvent(appt);
      this.selectedEvents.push(event);
    });
  }

  cancelAppointment(appointmentId: number): Observable<any> {
    var appointment = {
      appointmentId: appointmentId,
      status: AppointmentStatus.APPOINTMENT_CANCELLED,
      slotId: 0,
    };
    return this.appointmentsService.updateAppointmentStatus(appointment);
  }

  confirmAppointment(appointmentId: number): Observable<any> {
    var appointment = {
      appointmentId: appointmentId,
      status: AppointmentStatus.APPOINTMENT_SCHEDULED,
      slotId: 0,
    };
    return this.appointmentsService.updateAppointmentStatus(appointment);
  }

  updateTime(appointmentTime: AppointmentTime): Observable<any> {
    return this.appointmentsService.updateAppointmentTime(appointmentTime);
  }

  assignAppointmentSlots(appointmentSlots: any): Observable<any> {
    return this.appointmentsService.assignAppointmentSlots(appointmentSlots);
  }

  createAppointment(data: CreateAppointment): Observable<any> {
    return this.appointmentsService.createAppointment(data);
  }

  setSelectedDayEvents(date: Date): void {
    this.noAppointmentsText = "No appointments confirmed for this day";

    if (!this.currentEvent) {
      this.clearSelectedEvents();
      this.selectedEvents = this.events.filter(
        (x) =>
          moment(x.start).isSame(date, "day") &&
          moment(x.start).isSame(date, "month") &&
          moment(x.start).isSame(date, "year") &&
          x.appointment &&
          x.appointment.status === AppointmentStatus.APPOINTMENT_SCHEDULED
      );
      this.setSelectedDay(date);
    }
    this.setEventTitle(date);
  }

  setSelectedEvent(event: CustomCalendarEvent): void {
    this.clearSelectedEvents();
    this.currentEvent = event;

    if (this.currentEvent.appointment.duration === 0) {
      this.currentEvent.appointment.duration = 30;
    }
    if (event.referral) {
      const appt = this.appointments.find(
        (x) => x.id == event.referral.appointmentId
      );
      if (appt) {
        event.appointment = appt;
      }
    }
    this.selectedCalendarView = CalendarView.Day;
    this.selectedEvents.push(event);

    this.selectEvent$.next(new Date());
    if (event.start) {
      this.setSelectedDay(event.start);
      this.selectEvent$.next(event.start);
    }

    this.hasDeclinedEvents =
      this.declinedEvents.filter(
        (x) => x.appointment.id === event.appointment.id
      ).length > 0;

    this.selectedEventsTitle = "Appointment Setup";
  }

  setSelectedDay(date: Date): void {
    this.setEventTitle(date);
    this.selectedDate = date;
    this.selectedCalendarView = CalendarView.Day;
  }

  clearSelectedEvents(): void {
    this.closeFilter();
    this.selectedEvents = undefined;
    this.selectedEvents = [];
  }

  clearSuggestedEvents(): void {
    this.onTimeEvents = undefined;
    this.onTimeEvents = [];
    this.events = this.events.filter(
      (x) => x.cssClass.indexOf("suggested") === -1
    );
  }

  clearRescheduleEvents(): void {
    this.isRescheduling = false;
    this.onTimeEvents = undefined;
    this.onTimeEvents = [];
    this.events = this.events.filter(
      (x) => x.cssClass.indexOf("reschedule") === -1
    );
  }

  clearNewEvents(): void {
    this.onTimeEvents = undefined;
    this.onTimeEvents = [];
    this.events = this.events.filter((x) => x.cssClass.indexOf("new") === -1);
  }

  clearEvents(): void {
    this.clearRescheduleEvents();
    this.clearSuggestedEvents();
    this.clearNewEvents();
  }

  closeFilter(): void {
    // localStorage.removeItem('appointmentFilterSelected');
    this.currentFilter = "";
    this.currentEvent = undefined;
  }

  initAppointmentsService(): void {
    this.totalNewAppointments =
      this.totalReferralAppointments =
      this.totalEstablishedAppointments =
        0;
    this.getTotalAppointmentsNumber();
    this.clearSelectedEvents();
    this.clearEvents();
  }

  destroyAppointmentsService(): void {
    this.initAppointmentsService();
    localStorage.removeItem("appointmentFilterSelected");
    localStorage.removeItem("patientSelected");
  }

  addSuggestEventList(date: Date) {
    if (
      this.currentEvent.appointment &&
      this.currentEvent.appointment.appointmentAllottedSlots.length > 0
    ) {
      var slotHasBeenRejected =
        this.currentEvent.appointment.appointmentAllottedSlots.find(
          (x) =>
            x.isDeclined &&
            moment(x.startDateTime).toISOString() === moment(date).toISOString()
        );
      if (slotHasBeenRejected) {
        this.snackBar.open(
          "The selected time has been declined by the patient",
          "Ok"
        );
        return;
      }
    }

    var exists = this.onTimeEvents.filter(
      (x) => x.start.toTimeString() === date.toTimeString()
    );
    if (exists.length > 0) {
      return;
    }
    const event: CustomCalendarEvent = {
      start: date,
      end: moment(date)
        .add(this.currentEvent.appointment.duration, "minutes")
        .toDate(),
      title:
        this.currentEvent.appointment.patient.firstName +
        " " +
        this.currentEvent.appointment.patient.lastName,
      resizable: { beforeStart: false, afterEnd: false },
      draggable: false,
      meta: { incrementsBadgeTotal: true },
      cssClass: `custom-event suggested ${this.currentEvent.appointment.status.toLowerCase()}`,
      appointment: this.currentEvent.appointment,
    };

    this.onTimeEvents.push(event);
    this.onTimeEvents = this.onTimeEvents.sort((a, b) =>
      a.start.toISOString() < b.start.toISOString() ? -1 : 1
    );
    this.events.push(event);
    this.refreshCalendar.next();
  }

  removeSuggestEventList(event: CustomCalendarEvent): void {
    this.onTimeEvents = this.onTimeEvents.filter(
      (x) => x.start !== event.start
    );
    this.events = this.events.filter((x) => x.start !== event.start);
    this.refreshCalendar.next();
  }

  addRescheduleEvent(date: Date) {
    this.events = this.events.filter(
      (x) => x.cssClass.indexOf("reschedule") === -1
    );

    const event: CustomCalendarEvent = {
      start: date,
      end: moment(date)
        .add(this.currentEvent.appointment.duration, "minutes")
        .toDate(),
      title:
        this.currentEvent.appointment.patient.firstName +
        " " +
        this.currentEvent.appointment.patient.lastName,
      resizable: { beforeStart: false, afterEnd: false },
      draggable: false,
      meta: { incrementsBadgeTotal: true },
      cssClass: "custom-event reschedule",
      appointment: this.currentEvent.appointment,
    };

    this.onTimeEvents.push(event);
    this.onTimeEvents = this.onTimeEvents.sort((a, b) =>
      a.start.toISOString() < b.start.toISOString() ? -1 : 1
    );
    this.events.push(event);
    this.refreshCalendar.next();
  }

  addNewAppointmentList(date: Date) {
    this.onTimeEvents = [];
    this.events = this.events.filter((x) => x.cssClass.indexOf("new") === -1);

    const event: CustomCalendarEvent = {
      start: date,
      end: moment(date)
        .add(this.currentEvent.appointment.duration, "minutes")
        .toDate(),
      title:
        this.currentEvent.appointment.patient.firstName +
        " " +
        this.currentEvent.appointment.patient.lastName,
      resizable: { beforeStart: false, afterEnd: false },
      draggable: false,
      meta: { incrementsBadgeTotal: true },
      cssClass: "custom-event mini new",
      appointment: this.currentEvent.appointment,
    };

    this.onTimeEvents.push(event);
    this.onTimeEvents = this.onTimeEvents.sort((a, b) =>
      a.start.toISOString() < b.start.toISOString() ? -1 : 1
    );
    this.events.push(event);
    this.refreshCalendar.next();
  }

  selectPatient(patient: Patient): void {
    const appointmentExists = this.events.filter(
      (x) =>
        x.appointment &&
        x.appointment.patient.id === patient.id &&
        moment(x.start).isSameOrAfter(moment(new Date()))
    );

    if (appointmentExists.length > 0) {
      const lastEvent = appointmentExists[appointmentExists.length - 1];
      this.setSelectedEvent(lastEvent);
    } else {
      const appointment = new Appointment();
      appointment.patient = patient;
      appointment.healthSystem = this.physicianOptionService.getHealthSystem();
      appointment.practitioner = this.physicianOptionService.getPhysician();
      appointment.status = AppointmentStatus.NEW_APPOINTMENT;
      appointment.duration = 30;

      const event = {
        start: new Date(),
        title: patient.firstName + " " + patient.lastName,
        resizable: { beforeStart: false, afterEnd: false },
        draggable: false,
        meta: { incrementsBadgeTotal: false },
        cssClass: "custom-event new",
        appointment: appointment,
      };

      this.setSelectedEvent(event);
    }
  }

  selectReferral(): void {
    const appointment = new Appointment();
    appointment.healthSystem = this.physicianOptionService.getHealthSystem();
    appointment.practitioner = this.physicianOptionService.getPhysician();
    appointment.status = AppointmentStatus.PATIENT_AVAILABILITY_SET;
    appointment.duration = 30;

    this.referrals.forEach((ref) => {
      appointment.patient = ref.patient;
      appointment.symptom = ref.symptoms;
      const clonedAppointment = JSON.parse(JSON.stringify(appointment));
      let event: CustomCalendarEvent = {
        start: new Date(new Date()),
        title: ref.patient.firstName + " " + ref.patient.lastName,
        resizable: { beforeStart: false, afterEnd: false },
        draggable: false,
        meta: { incrementsBadgeTotal: true },
        cssClass: "custom-event",
        appointment: clonedAppointment,
        referral: ref,
      };
      this.selectedEvents.push(event);
    });
  }

  private CreateEventList(appointments: Appointment[]): void {
    this.declinedEvents = [];
    appointments.forEach((appointment) => {
      switch (appointment.status) {
        case AppointmentStatus.PROVIDER_AVAILABILITY_SET:
          appointment.appointmentAllottedSlots.forEach((availSlot) => {
            const event = this.createEvent(appointment, availSlot);
            if (availSlot.isDeclined) {
              this.declinedEvents.push(event);
            } else if (!this.hasDuplicated(appointment, availSlot)) {
              this.events.push(event);
            }
          });
          break;
        case AppointmentStatus.PATIENT_AVAILABILITY_SET:
          if (
            appointment.date &&
            appointment.startTime &&
            appointment.endTime
          ) {
            const event = this.createEvent(appointment);
            if (!this.hasDuplicated(appointment)) {
              this.events.push(event);
            }
          } else {
            appointment.appointmentAllottedSlots.forEach((availSlot) => {
              const event = this.createEvent(appointment, availSlot);
              if (availSlot.isDeclined) {
                this.declinedEvents.push(event);
              } else if (!this.hasDuplicated(appointment, availSlot)) {
                this.events.push(event);
              }
            });
          }
          break;
        case AppointmentStatus.PATIENT_SELECTED_APPOINTMENTSLOT:
          if (
            appointment.date &&
            appointment.startTime &&
            appointment.endTime
          ) {
            const event = this.createEvent(appointment);
            if (!this.hasDuplicated(appointment)) {
              this.events.push(event);
            }
          }
          break;
        case AppointmentStatus.APPOINTMENT_SCHEDULED:
          if (
            appointment.date &&
            appointment.startTime &&
            appointment.endTime
          ) {
            const event = this.createEvent(appointment);
            if (!this.hasDuplicated(appointment)) {
              this.events.push(event);
            }
          }
          break;
        default:
          break;
      }
    });
  }

  private createEvent(
    appointment: Appointment,
    appointmentSlot: AppointmentSlots = undefined
  ): CustomCalendarEvent {
    let event: CustomCalendarEvent;

    if (appointmentSlot) {
      event = {
        start: moment(appointmentSlot.startDateTime).utc(false).toDate(),
        end: moment(appointmentSlot.endDateTime).utc(false).toDate(),
        title:
          appointment.patient.firstName + " " + appointment.patient.lastName,
        resizable: { beforeStart: false, afterEnd: false },
        draggable: false,
        meta: { incrementsBadgeTotal: true },
        cssClass: "custom-event",
        appointment: appointment,
      };
    } else {
      event = {
        start: moment(appointment.startDateTime).utc(false).toDate(),
        end: moment(appointment.endDateTime).utc(false).toDate(),
        title:
          appointment.patient.firstName + " " + appointment.patient.lastName,
        resizable: { beforeStart: false, afterEnd: false },
        draggable: false,
        meta: { incrementsBadgeTotal: true },
        cssClass: "custom-event",
        appointment: appointment,
      };

      if (!appointment.startTime) {
        (event.start = moment(appointment.date).utc(false).toDate()),
          (event.end = undefined);
      }
    }

    if (appointmentSlot) {
      if (moment(appointmentSlot.startDateTime).isBefore(moment())) {
        event.cssClass += " past-event";
      }
    } else {
      if (
        moment(
          moment(appointment.date.split("T")[0] + " " + appointment.startTime)
        ).isBefore(moment())
      ) {
        event.cssClass += " past-event";
      }
    }

    switch (appointment.status) {
      case AppointmentStatus.PROVIDER_AVAILABILITY_SET:
        event.color = { primary: "#ffd40a", secondary: "#ffd40a" };
        event.cssClass += " mini not-active";
        break;
      case AppointmentStatus.PATIENT_AVAILABILITY_SET:
        event.color = { primary: "#ffd40a", secondary: "#ffd40a" };
        if (appointmentSlot) {
          event.cssClass += " yellow";
          break;
        }
        event.cssClass += " mini patient-availability-set";
        break;
      case AppointmentStatus.PATIENT_SELECTED_APPOINTMENTSLOT:
        event.color = { primary: "#9b9b9b", secondary: "#9b9b9b" };
        event.cssClass += " mini not-accepted";
        break;
      case AppointmentStatus.APPOINTMENT_SCHEDULED:
        event.color = { primary: "#5dac06", secondary: "#5dac06" };
        event.resizable = { beforeStart: true, afterEnd: true };
        event.draggable = true;
        event.cssClass += " scheduled";
        break;
      default:
        break;
    }

    return event;
  }

  private hasDuplicated(
    appointment: Appointment,
    appointmentSlot: AppointmentSlots = undefined
  ): boolean {
    if (appointmentSlot) {
      const existAppointmentSlot = this.events.filter(
        (x) =>
          x.appointment &&
          x.appointment.appointmentAllottedSlots.find(
            (y) =>
              y.id === appointmentSlot.id &&
              x.start === moment(y.startDateTime).utc(false).toDate()
          )
      );
      if (existAppointmentSlot.length > 0) {
        return true;
      }
    } else {
      const existAppointment = this.events.filter(
        (x) => x.appointment && x.appointment.id === appointment.id
      );
      if (existAppointment.length > 0) {
        return true;
      }
    }
    return false;
  }

  private createDayOffEvent(dayOff: PractitionerDayOff): void {
    const days = differenceInCalendarDays(
      moment(dayOff.endDate).toDate(),
      moment(dayOff.startDate).toDate()
    );

    if (days === 0) {
      const event: CustomCalendarEvent = {
        start: moment(dayOff.startDateStartTime).toDate(),
        end: moment(dayOff.endDateEndTime).toDate(),
        title: dayOff.reason,
        resizable: { beforeStart: false, afterEnd: false },
        draggable: false,
        meta: { incrementsBadgeTotal: false },
        cssClass: "custom-event dayoff-event",
        appointment: undefined,
        dayOff: dayOff,
      };
      this.events.push(event);
    } else {
      for (let index = 0; index < days; index++) {
        const event: CustomCalendarEvent = {
          start: moment(dayOff.startDateStartTime).add(index, "days").toDate(),
          end: moment(dayOff.startDateEndTime).add(index, "days").toDate(),
          title: dayOff.reason,
          resizable: { beforeStart: false, afterEnd: false },
          draggable: false,
          meta: { incrementsBadgeTotal: false },
          cssClass: "custom-event dayoff-event",
          appointment: undefined,
          dayOff: dayOff,
        };

        this.events.push(event);
      }
    }
  }

  private setEventTitle(date: Date): void {
    if (!this.currentEvent) {
      this.selectedEventsTitle =
        date.toDateString() === new Date().toDateString()
          ? "Today's"
          : moment(date).format("MM/DD/YYYY");
    }
  }

  private getAppointmentsNumber(appointments: Appointment[]): void {
    // this.totalAppointments = appointments.filter(appt => moment(appt.date).isSameOrAfter(moment(new Date()).format('MM/DD/YYYY')) && (appt.status !== AppointmentStatus.APPOINTMENT_CANCELLED && appt.status !== AppointmentStatus.APPOINTMENT_SCHEDULED)).length;
    this.totalNewAppointments = appointments.filter(
      (appt) =>
        appt.type === "NEW" &&
        moment(appt.date).isSameOrAfter(
          moment(new Date()).format("MM/DD/YYYY")
        ) &&
        appt.status !== AppointmentStatus.APPOINTMENT_CANCELLED &&
        appt.status !== AppointmentStatus.APPOINTMENT_SCHEDULED
    ).length;
    this.totalEstablishedAppointments = appointments.filter(
      (appt) =>
        appt.type === "ESTABLISHED" &&
        moment(appt.date).isSameOrAfter(
          moment(new Date()).format("MM/DD/YYYY")
        ) &&
        appt.status !== AppointmentStatus.APPOINTMENT_CANCELLED &&
        appt.status !== AppointmentStatus.APPOINTMENT_SCHEDULED
    ).length;
    // this.totalReferralAppointments = appointments.filter(appt => appt.type === 'REFERRAL' && moment(appt.date).isSameOrAfter(moment(new Date()).format('MM/DD/YYYY')) && (appt.status !== AppointmentStatus.APPOINTMENT_CANCELLED && appt.status !== AppointmentStatus.APPOINTMENT_SCHEDULED)).length;
    this.getTotalAppointmentsNumber();
  }

  private getTotalAppointmentsNumber() {
    this.totalAppointments =
      this.totalNewAppointments +
      this.totalReferralAppointments +
      this.totalEstablishedAppointments;
  }
}
